import PropTypes from "prop-types";
import { useState } from "react";
import styled from "styled-components";
import { Icons } from "../../component";

type AvatarSize = "small" | "medium" | "large";
type PlatformIconName =
  | "facebook"
  | "googlemybusiness"
  | "instagram"
  | "linkedin"
  | "pinterest"
  | "snapchat"
  | "tiktok"
  | "other"
  | "threads"
  | "twitter"
  | "youtube";

const platformTypeIconMap: Record<string, PlatformIconName> = {
  fb: "facebook",
  facebook: "facebook",
  gmb: "googlemybusiness",
  googlebusiness: "googlemybusiness",
  googlemybusiness: "googlemybusiness",
  ig: "instagram",
  instagram: "instagram",
  li: "linkedin",
  linkedin: "linkedin",
  ot: "other",
  other: "other",
  pi: "pinterest",
  pinterest: "pinterest",
  sc: "snapchat",
  snapchat: "snapchat",
  th: "threads",
  threads: "threads",
  tt: "tiktok",
  tiktok: "tiktok",
  tw: "twitter",
  twitter: "twitter",
  x: "twitter",
  yt: "youtube",
  youtube: "youtube",
};

export function getPlatformIconName(platformType?: string): PlatformIconName {
  const normalizedPlatform = (platformType || "other")
    .toLowerCase()
    .replaceAll(/[^a-z0-9]/g, "");

  return platformTypeIconMap[normalizedPlatform] || "other";
}

function AccountAvatar({
  profilePictureUrl,
  platformType,
  color,
  size = "medium",
  className = "",
  disableAltText,
  disconnected,
  name,
}: {
  profilePictureUrl?: string | null;
  platformType: string;
  color?: string;
  size?: AvatarSize;
  tooltip?: string;
  className?: string;
  disableAltText?: boolean;
  disconnected?: boolean;
  name?: string;
}) {
  const [failedProfilePictureUrl, setFailedProfilePictureUrl] = useState<
    string | null
  >(null);
  const showProfilePicture =
    Boolean(profilePictureUrl) && profilePictureUrl !== failedProfilePictureUrl;
  const avatarColor = color || "#223e9c";
  const letters = !showProfilePicture && name ? getAccountShortName(name) : "";
  const platformName = getPlatformIconName(platformType);

  return (
    <StyledAvatar
      $size={size}
      $color={showProfilePicture ? color : avatarColor}
      className={className}
      data-testid="account-avatar"
    >
      {showProfilePicture && profilePictureUrl && (
        <img
          className="rounded-avatar"
          src={profilePictureUrl}
          onError={() => setFailedProfilePictureUrl(profilePictureUrl)}
          alt={disableAltText ? "" : "Profile image"}
          title="Profile image"
          referrerPolicy="no-referrer"
        />
      )}
      {letters && (
        <div
          className="rounded-avatar"
          style={{ backgroundColor: avatarColor, position: "relative" }}
        >
          <LetterText size={size}>{letters}</LetterText>
        </div>
      )}
      <IconWrap>
        <PlatformIcon className={`platform-icon-${platformName}`} />
      </IconWrap>
      {disconnected && (
        <DisconnectWarningIconContainer>
          <Icons name="warningAlert" size={14} />
        </DisconnectWarningIconContainer>
      )}
    </StyledAvatar>
  );
}

const IconWrap = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
`;

const LetterText = styled.div<{ size: AvatarSize }>`
  font-size: ${(p) => {
    switch (p.size) {
      case "small": {
        return "14px";
      }
      case "medium": {
        return "22.5px";
      }
      case "large": {
        return "22.5px";
      }
    }
  }};
  font-weight: 500;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const DisconnectWarningIconContainer = styled.div`
  color: #fa4f38;
  position: absolute;
  top: -5px;
  left: -5px;
  margin-left: auto;
`;

export type avatarSizeModel = {
  width: string;
  height: string;
  imgSize: string;
  border: string;
  platform: string;
};
const avatarSizes: {
  [key in AvatarSize]: avatarSizeModel;
} = {
  small: {
    width: "30px",
    height: "30px",
    imgSize: "27px",
    border: "3px",
    platform: "15px",
  },
  medium: {
    width: "46px",
    height: "46px",
    imgSize: "43px",
    border: "4px",
    platform: "20px",
  },
  large: {
    width: "50px",
    height: "50px",
    imgSize: "50px",
    border: "4px",
    platform: "20px",
  },
};

const StyledAvatar = styled.div<{
  $size: AvatarSize;
  $color?: string;
  className: string;
  "data-testid": string;
}>`
  display: inline-block;
  width: ${({ $size }) => avatarSizes[$size].width};
  height: ${({ $size }) => avatarSizes[$size].height};
  min-width: ${({ $size }) => avatarSizes[$size].width};
  min-height: ${({ $size }) => avatarSizes[$size].height};
  position: relative;
  input {
    display: none;
  }
  .rounded-avatar {
    width: ${({ $size }) => avatarSizes[$size].imgSize};
    height: ${({ $size }) => avatarSizes[$size].imgSize};
    min-width: ${({ $size }) => avatarSizes[$size].imgSize};
    min-height: ${({ $size }) => avatarSizes[$size].imgSize};
    border-radius: 50%;
    ${({ $color, $size }) =>
      `border: ${avatarSizes[$size].border} solid ${
        $color ? $color : "rgba(0,0,0,0)"
      };`}
  }
  img.rounded-avatar {
    background-color: #fff;
  }
  i {
    position: absolute;
    right: 0;
    bottom: 0;
    width: ${({ $size }) => avatarSizes[$size].platform};
    height: ${({ $size }) => avatarSizes[$size].platform};
  }
`;

AccountAvatar.propTypes = {
  profilePictureUrl: PropTypes.string,
  platformType: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  placement: PropTypes.string,
  tooltip: PropTypes.string,
  className: PropTypes.string,
};

export const PlatformIcon = styled.i`
  display: inline-block;
  width: 18px;
  height: 18px;
  background-image: url("/assets/insights-assets/images/social-squared-icons-sprite.png");
  overflow: hidden;
  background-size: 512%;

  &.platform-icon-instagram,
  &.platform-icon-IG,
  &.platform-icon-ig {
    background-position: 51.5% 0.97%;
  }

  &.platform-icon-facebook,
  &.platform-icon-FB,
  &.platform-icon-fb {
    background-position: 0.97% 0.97%;
  }

  &.platform-icon-twitter,
  &.platform-icon-TW,
  &.platform-icon-tw {
    background-position: 76.7% 26.2%;
  }

  &.platform-icon-pinterest,
  &.platform-icon-PI,
  &.platform-icon-pi {
    background-position: 0.97% 26.2%;
  }

  &.platform-icon-linkedin,
  &.platform-icon-LI,
  &.platform-icon-li {
    background-position: 76.7% 0.97%;
  }

  &.platform-icon-googlemybusiness,
  &.platform-icon-GMB,
  &.platform-icon-gmb {
    background-position: 26.2% 0.97%;
  }

  &.platform-icon-snapchat,
  &.platform-icon-SC,
  &.platform-icon-sc {
    background-position: 26.2% 26.2%;
  }

  &.platform-icon-tiktok,
  &.platform-icon-TT,
  &.platform-icon-tt {
    background-position: 51.5% 26.2%;
  }

  &.platform-icon-youtube,
  &.platform-icon-YT,
  &.platform-icon-yt {
    background-position: 0.97% 51.5%;
  }

  &.platform-icon-threads,
  &.platform-icon-TH,
  &.platform-icon-th {
    background-position: 26.2% 50.5%;
  }

  z-index: 9 !important;
`;

function getAccountShortName(name: string): string {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .filter((letter) => /[a-zA-Z]/.test(letter))
    .join("");
}

export default AccountAvatar;
